import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Camera, RotateCcw, Check, X } from 'lucide-react';

export const CameraCapture = ({ onCapture, onClose, facingMode = 'user', label }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [captured, setCaptured] = useState(null);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      setCaptured(null);
      setError(null);
      const constraints = {
        video: { facingMode, width: { ideal: 640 }, height: { ideal: 480 } }
      };
      const s = await navigator.mediaDevices.getUserMedia(constraints);
      setStream(s);
      if (videoRef.current) {
        videoRef.current.srcObject = s;
      }
    } catch (e) {
      console.error('Camera error:', e);
      setError('Camera access denied. Please allow camera permissions in your browser settings.');
    }
  }, [facingMode]);

  useEffect(() => {
    startCamera();
    if (facingMode === 'user' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        (err) => console.warn('Geolocation error:', err),
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }
    return () => {
      if (stream) stream.getTracks().forEach(t => t.stop());
    };
  }, []);

  const capturePhoto = () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const data = canvas.toDataURL('image/jpeg', 0.6);
    setCaptured(data);
    if (stream) stream.getTracks().forEach(t => t.stop());
  };

  const confirmPhoto = () => {
    onCapture(captured, location);
  };

  const retake = () => {
    setCaptured(null);
    startCamera();
  };

  return (
    <div data-testid="camera-capture" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-900">{label || 'Capture Photo'}</h3>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
          <br />
          <Button variant="outline" size="sm" onClick={startCamera} className="mt-2">
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Try Again
          </Button>
        </div>
      ) : captured ? (
        <div className="space-y-3">
          <div className="rounded-lg overflow-hidden border border-border">
            <img src={captured} alt="Captured" className="w-full" />
          </div>
          {location && (
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
              Location: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
            </div>
          )}
          <div className="flex gap-2">
            <Button data-testid="retake-photo-btn" variant="outline" onClick={retake} className="flex-1">
              <RotateCcw className="w-4 h-4 mr-1" /> Retake
            </Button>
            <Button data-testid="confirm-photo-btn" onClick={confirmPhoto} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">
              <Check className="w-4 h-4 mr-1" /> Confirm
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="rounded-lg overflow-hidden border border-border bg-black relative" style={{ minHeight: 300 }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full"
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
            />
          </div>
          {location && (
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-2">
              Location: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
            </div>
          )}
          <Button data-testid="capture-photo-btn" onClick={capturePhoto} className="w-full bg-amber-600 hover:bg-amber-700 text-white">
            <Camera className="w-4 h-4 mr-2" /> Capture
          </Button>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
